import { cn } from '@eureca/ui';
import { Link, useSearch } from '@tanstack/react-router';
import { logoutToLogin } from '@/auth/logout';
import { EurecaLogo } from './EurecaLogo';
import {
  ArchiveIcon,
  CircleHelpIcon,
  ClipboardListIcon,
  DoorOpenIcon,
  HouseIcon,
  MoonIcon,
  RouteIcon,
  SunIcon,
} from './icons';
import { useState, type ComponentType } from 'react';

type IconComponent = ComponentType<{ className?: string }>;

interface NavItem {
  id: ConsultantsMenuItemId;
  label: string;
  Icon: IconComponent;
  to?: string;
  /**
   * Workspace-scoped destination — the current `?companyId=` (área de trabalho)
   * is carried along so navigating between the internal pages preserves the
   * selected company (and doesn't trip the workspace guard).
   */
  scoped?: boolean;
}

export type ConsultantsMenuItemId = 'home' | 'programs' | 'jobs' | 'forms' | 'help' | 'logout';

// Per spec 025, the home `/` IS the companies listing — there is no separate
// "Empresas" entry. The previous `/companies` route was removed and consumers
// (CompaniesPage, etc.) all pass `activeId="home"`.
const primaryItems: NavItem[] = [
  // The home (`/`) IS the companies listing (spec 025 dropped the
  // /workspaces split). Pointing both icons at `/` would highlight two
  // entries as active; we keep `home` as the single entry until a
  // dedicated /companies route exists.
  { id: 'home', label: 'Áreas de trabalho', Icon: HouseIcon, to: '/' },
  { id: 'programs', label: 'Programas', Icon: ArchiveIcon, to: '/programas', scoped: true },
  { id: 'jobs', label: 'Vagas', Icon: RouteIcon, to: '/vagas', scoped: true },
  { id: 'forms', label: 'Formulários', Icon: ClipboardListIcon, to: '/formularios', scoped: true },
  { id: 'help', label: 'Ajuda', Icon: CircleHelpIcon },
];

const footerItems: NavItem[] = [{ id: 'logout', label: 'Sair', Icon: DoorOpenIcon }];

// Tally help/feedback channel opened by the "?" menu item.
const HELP_CHANNEL_URL = 'https://tally.so/r/w4OdxY';

function openHelpChannel(): void {
  window.open(HELP_CHANNEL_URL, '_blank', 'noopener,noreferrer');
}

export interface ConsultantsMenuProps {
  activeId?: ConsultantsMenuItemId;
  onLogout?: () => void;
  onHelp?: () => void;
}

/**
 * "Consultants menu" sidebar — Figma node 13132:464.
 * Collapsed/icon-only variant matching the Home design.
 */
export function ConsultantsMenu({
  // Defaults to the real Keycloak logout so the "Sair" button works on EVERY
  // page — previously only the pages that explicitly passed `onLogout`
  // (Companies, Jobs list) had a working button; everywhere else it was a
  // no-op (`onClick={undefined}`). Pass `onLogout` to override (stories/tests).
  activeId = 'home',
  onLogout = logoutToLogin,
  // Same story for "?" (EU-421): only the Home wired `onHelp`, so the button
  // was a no-op on every other page. Defaulting it here makes it redirect to
  // the Tally channel identically across all routes. Pass `onHelp` to override.
  onHelp = openHelpChannel,
}: ConsultantsMenuProps = {}) {
  // Current workspace (área de trabalho). `strict: false` reads `companyId` from
  // whatever route is active without tying the sidebar to a single route type.
  const { companyId } = useSearch({ strict: false }) as { companyId?: string };
  return (
    <aside
      aria-label="Menu do consultor"
      // Docked to the left edge: `sticky top-0` + `h-dvh` keeps the sidebar
      // visible across the full viewport as the main column scrolls. Uses
      // `bg-card` (white in light, lifted dark in dark) so the navbar sits
      // above the muted page background — pages got swapped to `bg-muted`
      // so the navbar is the elevated surface in both themes.
      className="sticky top-0 hidden h-dvh shrink-0 flex-col items-center justify-between border-r border-border bg-card px-6 py-8 md:flex"
    >
      <div className="flex flex-col items-center gap-10">
        <EurecaLogo aria-label="Eureca" className="h-4 w-auto text-primary" />
        <nav aria-label="Navegação principal">
          <ul className="flex flex-col items-center gap-2">
            {primaryItems.map((item) => (
              <li key={item.id}>
                <NavButton
                  item={item}
                  active={item.id === activeId}
                  companyId={companyId}
                  onClick={item.id === 'help' ? onHelp : undefined}
                />
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <nav aria-label="Ações da conta">
        <ul className="flex flex-col items-center gap-2">
          {/* Theme switcher — available in local dev and on stg, hidden in
              prd. Both `import.meta.env.DEV` and `import.meta.env.VITE_APP_ENV`
              are Vite compile-time constants (VITE_* is statically replaced
              with its literal at build), so in the prd build the test folds to
              `false` and the branch is dead-code-eliminated — it can never
              sneak into production. `VITE_APP_ENV` is set per-environment by
              deploy-frontends.yml. */}
          {import.meta.env.DEV || import.meta.env.VITE_APP_ENV === 'stg' ? (
            <li>
              <ThemeToggleButton />
            </li>
          ) : null}
          {footerItems.map((item) => (
            <li key={item.id}>
              <NavButton
                item={item}
                active={item.id === activeId}
                onClick={item.id === 'logout' ? onLogout : undefined}
              />
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
}

function ThemeToggleButton() {
  // Seed from the actual class on <html> so a hot-reload doesn't desync the
  // icon from the live state.
  const [isDark, setIsDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );

  function toggle() {
    const next = document.documentElement.classList.toggle('dark');
    setIsDark(next);
  }

  const Icon = isDark ? SunIcon : MoonIcon;
  const label = isDark ? 'Mudar para modo claro' : 'Mudar para modo escuro';

  return (
    <button
      type="button"
      aria-label={label}
      onClick={toggle}
      className="flex size-8 items-center justify-center rounded-md text-foreground transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <Icon className="size-4" />
    </button>
  );
}

function NavButton({
  item,
  active,
  companyId,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  companyId?: string;
  onClick?: () => void;
}) {
  const { Icon, label, to, scoped } = item;
  const className = cn(
    'flex size-8 items-center justify-center rounded-md transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
    active ? 'bg-secondary text-secondary-foreground' : 'text-foreground hover:bg-accent/50'
  );

  if (to) {
    // Workspace-scoped links carry the current `companyId` so switching between
    // Programas/Vagas/Formulários keeps the selected área de trabalho.
    const search = scoped && companyId ? { companyId } : undefined;
    return (
      <Link
        to={to}
        search={search}
        aria-label={label}
        aria-current={active ? 'page' : undefined}
        className={className}
      >
        <Icon className="size-4" />
      </Link>
    );
  }

  return (
    <button
      type="button"
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={className}
      onClick={onClick}
    >
      <Icon className="size-4" />
    </button>
  );
}
